import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import api, { apiErr } from "../lib/api";
import { ROLE_LABELS, waLink, openWa } from "../lib/format";
import { SearchBox, matchQ } from "../components/SearchBox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { UserCheck, Check, X, Eye, Clock } from "lucide-react";
import { toast } from "sonner";

const ASIGNABLES = ["cliente", "repartidor", "encargado", "encargado_comisionista", "comisionista", "punto"];
const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

export default function Aprobaciones() {
  const { setMobileOpen } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [ver, setVer] = useState(null);
  const [aprob, setAprob] = useState(null); // usuario en proceso de aprobación
  const [role, setRole] = useState("cliente");
  const [sucursal, setSucursal] = useState("ASU");
  const [sucs, setSucs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => { api.get("/users/pendientes").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/sucursales?activas=true").then(({ data }) => setSucs(data.map((s) => s.code))).catch(() => {}); }, []);

  const abrirAprobar = (u) => { setVer(null); setRole(u.role || "cliente"); setSucursal(u.sucursal || "ASU"); setAprob(u); };

  const confirmarAprobacion = async () => {
    if (role !== "cliente" && !sucursal) { toast.error("Asigná una sucursal para este rol"); return; }
    setSaving(true);
    try {
      const payload = { role, sucursal: role === "cliente" ? null : sucursal };
      const { data } = await api.post(`/users/${aprob.id}/aprobar`, payload);
      toast.success(`${aprob.name} aprobado como ${ROLE_LABELS[data.role] || data.role}`);
      const wa = data.whatsapp || aprob.whatsapp;
      const sucTxt = data.sucursal ? ` para la sucursal ${data.sucursal}` : "";
      if (wa) {
        const msg = `¡Hola ${data.name || aprob.name}! Tu cuenta en Pya'elino Courier fue APROBADA ✅ con el rol de ${ROLE_LABELS[data.role] || data.role}${sucTxt}. Ya podés ingresar en ${window.location.origin}/login con tu email y contraseña.`;
        openWa(waLink(wa, msg));
      } else {
        toast.info("El usuario no cargó WhatsApp; no se pudo enviar el aviso.");
      }
      setAprob(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const rechazar = async (u) => {
    try { await api.post(`/users/${u.id}/rechazar`); toast(`${u.name} rechazado`); setVer(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <Header title="Aprobaciones de cuentas" subtitle="Designá rol y sucursal al aprobar cada registro" onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nombre, email, WhatsApp o sucursal…" testid="aprob-search" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.filter(matchQ(q)).map((u) => (
            <div key={u.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`pendiente-card-${u.email}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center"><Clock className="h-5 w-5 text-[#F5A623]" /></div>
                <div className="min-w-0">
                  <div className="font-bold text-[#0A1F44] truncate">{u.name}</div>
                  <div className="text-xs text-[#64748B] truncate">{u.email}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-[#64748B]">WhatsApp: {u.whatsapp || "—"}</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {u.doc_frente ? <img src={u.doc_frente} alt="frente" className="h-20 w-full object-cover rounded-lg border border-slate-200" /> : <div className="h-20 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">Sin frente</div>}
                {u.doc_dorso ? <img src={u.doc_dorso} alt="dorso" className="h-20 w-full object-cover rounded-lg border border-slate-200" /> : <div className="h-20 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">Sin dorso</div>}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setVer(u)} data-testid={`ver-${u.email}`} className="flex items-center justify-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-lg py-2 px-3 hover:bg-slate-50"><Eye className="h-4 w-4" /></button>
                <button onClick={() => rechazar(u)} data-testid={`rechazar-${u.email}`} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 border border-rose-300 rounded-lg py-2 hover:bg-rose-50"><X className="h-4 w-4" /> Rechazar</button>
                <button onClick={() => abrirAprobar(u)} data-testid={`aprobar-${u.email}`} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg py-2"><Check className="h-4 w-4" /> Aprobar</button>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="col-span-full text-center py-16 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">
              <UserCheck className="h-10 w-10 mx-auto text-emerald-300 mb-2" />
              No hay cuentas pendientes de aprobación.
            </div>
          )}
        </div>
      </main>

      {/* Ver documentos */}
      <Dialog open={!!ver} onOpenChange={() => setVer(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{ver?.name}</DialogTitle><DialogDescription>{ver?.email}</DialogDescription></DialogHeader>
          {ver && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-[#64748B] mb-1">Documento (frente)</div>{ver.doc_frente ? <img src={ver.doc_frente} alt="frente" className="w-full rounded-lg border border-slate-200" /> : <div className="text-xs text-slate-400">No adjuntado</div>}</div>
                <div><div className="text-xs text-[#64748B] mb-1">Documento (dorso)</div>{ver.doc_dorso ? <img src={ver.doc_dorso} alt="dorso" className="w-full rounded-lg border border-slate-200" /> : <div className="text-xs text-slate-400">No adjuntado</div>}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => rechazar(ver)} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-rose-600 border border-rose-300 rounded-lg py-2.5 hover:bg-rose-50"><X className="h-4 w-4" /> Rechazar</button>
                <button onClick={() => abrirAprobar(ver)} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg py-2.5"><Check className="h-4 w-4" /> Aprobar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprobar con rol + sucursal */}
      <Dialog open={!!aprob} onOpenChange={(o) => { if (!o) setAprob(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Aprobar cuenta · {aprob?.name}</DialogTitle>
            <DialogDescription>Asigná el rol y, si corresponde, la sucursal del usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Rol</label>
              <select className={input} value={role} onChange={(e) => setRole(e.target.value)} data-testid="aprobar-role-select">
                {ASIGNABLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            {role !== "cliente" && (
              <div data-testid="aprobar-sucursal-wrap">
                <label className="text-xs font-semibold text-[#64748B]">Sucursal asignada</label>
                <select className={input} value={sucursal} onChange={(e) => setSucursal(e.target.value)} data-testid="aprobar-sucursal-select">
                  {sucs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-[#64748B]">Este usuario solo verá envíos e información de {sucursal}.</p>
              </div>
            )}
            <button onClick={confirmarAprobacion} disabled={saving} data-testid="aprobar-confirm-btn"
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 text-sm disabled:opacity-60">
              <Check className="h-4 w-4" /> {saving ? "Aprobando..." : "Aprobar y avisar por WhatsApp"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
